import React, { useState } from 'react';
import { Shield, Activity, Hash, CheckCircle, AlertTriangle, KeyRound } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from 'recharts';

import * as ed25519 from '@noble/ed25519';
import { sha3_256, sha3_512 } from '@noble/hashes/sha3';
import { sha512 } from '@noble/hashes/sha512';
import { concatBytes } from '@noble/hashes/utils';

// ---- REQUIRED: wire SHA-512 for noble-ed25519 (works in Vite/Browser) ----
const sha512Sync = (...msgs) => sha512(concatBytes(...msgs));

// compat: different noble-ed25519 versions read different hooks
if (ed25519.etc) {
  ed25519.etc.sha512Sync = sha512Sync;
  ed25519.etc.sha512Async = async (...msgs) => sha512Sync(...msgs);
}
if (ed25519.utils) {
  ed25519.utils.sha512Sync = sha512Sync;
  ed25519.utils.sha512Async = async (...msgs) => sha512Sync(...msgs);
}
if (ed25519.hashes) {
  ed25519.hashes.sha512 = sha512Sync;
}

// ---------- helpers ----------
const b64urlToBytes = (b64u) => {
  let s = (b64u || '').replace(/-/g, '+').replace(/_/g, '/');
  const pad = s.length % 4;
  if (pad) s += '='.repeat(4 - pad);
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

const b64urlDecodeJson = (seg) => {
  const bytes = b64urlToBytes(seg);
  const txt = new TextDecoder('utf-8').decode(bytes);
  return JSON.parse(txt);
};

const bytesToHex = (bytes) =>
  Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');

// RFC 8785 strict subset for QEAP
const canonicalize = (obj) => {
  if (obj === null) return 'null';
  if (typeof obj === 'boolean') return obj ? 'true' : 'false';

  if (typeof obj === 'number') {
    if (!Number.isInteger(obj)) throw new Error('Floats forbidden in QEAP');
    return obj.toString();
  }

  if (typeof obj === 'string') return JSON.stringify(obj);

  if (Array.isArray(obj)) {
    return '[' + obj.map(canonicalize).join(',') + ']';
  }

  if (typeof obj === 'object') {
    const keys = Object.keys(obj).sort(); // ASCII order
    return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalize(obj[k])).join(',') + '}';
  }

  throw new Error('Invalid type in JSON');
};

const calculateGrade = (payload) => {
  const v = payload.sovereignty_vector || {};
  const quorum = Number.isInteger(payload.quorum_count) ? payload.quorum_count : 1;
  const hasWitness =
    payload.has_witness === true ||
    (Number.isInteger(payload.witness_count) && payload.witness_count >= 1);

  const gateA = v.tau >= 9000 && v.sigma >= 9000;
  if (!gateA) return 'NONE';

  const gateAA = gateA && v.delta >= 5000 && quorum >= 3;
  const gateAAA = gateAA && v.lambda >= 8000 && hasWitness;

  if (gateAAA) return 'AAA';
  if (gateAA) return 'AA';
  return 'A';
};

async function fetchPubKeyBytes(kid) {
  const url = `/api/v2/qeap/keys/${encodeURIComponent(kid)}`;
  const res = await fetch(url);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Key resolution failed for kid=${kid} (HTTP ${res.status}) ${text}`);
  }
  const data = await res.json();
  if (!data?.pubkey_b64u) throw new Error(`Invalid key payload for kid=${kid}: missing pubkey_b64u`);
  return b64urlToBytes(data.pubkey_b64u);
}

// 🔥 QEAP Sovereign Verify Function
const verifyEd25519Signature = async ({ header, payloadCanonicalBytes, signatureB64u }) => {
  const kid = header?.kid;
  if (!kid) return { state: 'NOT_VERIFIED', detail: 'Missing kid' };

  try {
    const pubkey = await fetchPubKeyBytes(kid);

    // 🔥 QEAP sovereign hash: SHA3-512 (returns Uint8Array in @noble/hashes)
    const t = sha3_512(payloadCanonicalBytes);
    const sig = b64urlToBytes(signatureB64u);

    // verify(signature, messageHash, publicKey)
    const ok = await ed25519.verify(sig, t, pubkey);

    return ok
      ? { state: 'VERIFIED', detail: `QEAP sovereign verified (${kid})` }
      : { state: 'FAILED', detail: 'verify failed' };
  } catch (e) {
    return { state: 'FAILED', detail: e.message || 'Verification Error' };
  }
};

const RealityVerifier = () => {
  const [jws, setJws] = useState('');
  const [data, setData] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [sigState, setSigState] = useState({ state: 'NOT_VERIFIED', detail: '' });
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const verifyReceipt = async () => {
    try {
      setVerificationResult(null);
      setData(null);
      setSigState({ state: 'NOT_VERIFIED', detail: '' });

      const trimmed = (jws || '').trim();
      const parts = trimmed.split('.');
      if (parts.length !== 3) throw new Error('Format JWS invalide (doit avoir 3 parties)');

      const header = b64urlDecodeJson(parts[0]);
      const payload = b64urlDecodeJson(parts[1]);
      const signatureB64u = parts[2];

      // QEAP Header validation
      if (header.alg !== 'EdDSA') throw new Error("Algorithme non supporté (attendu: EdDSA)");
      if (header.typ !== 'QEAP+JWT') throw new Error("Type de token invalide (attendu: QEAP+JWT)");
      if (header.hash_alg !== 'SHA3-512') throw new Error("Hash Alg invalide (attendu: SHA3-512)");
      if (!header.kid) throw new Error('Header missing kid');

      // Integrity: receipt_hash = SHA3-256(canonical(payload_without_receipt_hash))
      if (!payload.receipt_hash) throw new Error('Payload sans receipt_hash');

      const payloadForHash = { ...payload };
      delete payloadForHash.receipt_hash;

      const canonicalJson = canonicalize(payloadForHash);
      const canonicalBytes = new TextEncoder().encode(canonicalJson);

      const calcReceiptHash = '0x' + bytesToHex(sha3_256(canonicalBytes));
      if (calcReceiptHash !== payload.receipt_hash) {
        throw new Error('INTEGRITY FAILURE: Receipt Hash mismatch');
      }

      // --- CRITICAL FIX: Signature is over the FULL payload (WITH receipt_hash) ---
      // Backend: sign_jws_compact(payload) -> includes receipt_hash
      const canonicalJsonSig = canonicalize(payload);
      const canonicalBytesSig = new TextEncoder().encode(canonicalJsonSig);

      const tForSig = sha3_512(canonicalBytesSig);

      // Log for debugging
      console.log('--- DEBUG VERIFICATION (NOBLE PRO) ---');
      console.log('KID:', header.kid);
      console.log('Pubkey (hex):', bytesToHex(await fetchPubKeyBytes(header.kid)));
      console.log('T (SHA3-512 of FULL payload):', '0x' + bytesToHex(tForSig));
      console.log('Sig (hex):', bytesToHex(b64urlToBytes(signatureB64u)));
      console.log('--- END DEBUG ---');

      // Verify Signature using helper
      const sigRes = await verifyEd25519Signature({
        header,
        payloadCanonicalBytes: canonicalBytesSig,
        signatureB64u
      });

      setSigState(sigRes);

      const grade = calculateGrade(payload);

      setData({
        ...payload,
        calculatedGrade: grade,
        _header: header,
        _integrity: {
          canonical_json: canonicalJson, // integrity uses partial payload
          calculated_receipt_hash: calcReceiptHash,
          hash_to_sign_sha3_512_hex: '0x' + bytesToHex(tForSig), // sig uses full payload
        },
      });

      setVerificationResult({
        success: true,
        msg: `Integrity Validated ✅ (receipt_hash). Signature: ${sigRes.state === 'VERIFIED' ? 'VERIFIED ✅' : 'FAILED ❌'}`,
      });

    } catch (e) {
      console.error(e);
      setVerificationResult({ success: false, msg: e.message || 'Unknown error' });
      setSigState({ state: 'NOT_VERIFIED', detail: e.message || '' });
    }
  };

  const sigColor =
    sigState.state === 'VERIFIED'
      ? 'text-emerald-300'
      : sigState.state === 'FAILED'
        ? 'text-red-300'
        : 'text-amber-300';

  return (
    <div className="p-6 bg-slate-900 text-slate-100 rounded-xl border border-slate-700 max-w-2xl w-full min-w-0">
      <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
        <Shield className="text-blue-400" /> Reality Verifier Public
        <button
          onClick={() => setIsInfoOpen(true)}
          style={{
            background: 'rgba(0, 255, 255, 0.1)', border: '1px solid #0ff', color: '#0ff',
            borderRadius: '50%', width: '24px', height: '24px', fontSize: '0.9rem',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, lineHeight: 1
          }}
          title="Comprendre le Vérificateur"
        >
          i
        </button>
      </h2>

      <textarea
        className="w-full h-32 bg-slate-800 border border-slate-700 rounded p-3 text-xs font-mono mb-4 focus:ring-2 focus:ring-blue-500 outline-none"
        placeholder="Collez le Reality Receipt (JWS) ici..."
        onChange={(e) => setJws(e.target.value)}
        value={jws}
      />

      <button
        onClick={verifyReceipt}
        className="w-full bg-blue-600 hover:bg-blue-500 py-2 rounded font-semibold transition-colors flex justify-center items-center gap-2"
      >
        <Activity size={16} /> Vérifier (Integrity + Ed25519)
      </button>

      {verificationResult && !verificationResult.success && (
        <div className="mt-4 p-3 bg-red-900/30 border border-red-500/50 rounded flex items-center gap-2 text-red-200">
          <AlertTriangle size={16} />
          <span className="font-mono text-xs">{verificationResult.msg}</span>
        </div>
      )}

      {verificationResult && verificationResult.success && (
        <div className="mt-4 p-3 bg-emerald-900/20 border border-emerald-500/40 rounded flex items-center gap-2 text-emerald-200">
          <CheckCircle size={16} />
          <span className="font-mono text-xs">{verificationResult.msg}</span>
        </div>
      )}

      {data && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-500">
          {/* Radar */}
          <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
            <h3 className="text-sm font-semibold mb-4 text-slate-400 uppercase tracking-wider">
              Vecteur de Souveraineté
            </h3>
            <div className="h-56 min-h-[224px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart
                  data={[
                    { axis: 'τ', value: (data.sovereignty_vector?.tau || 0) / 100 },
                    { axis: 'δ', value: (data.sovereignty_vector?.delta || 0) / 100 },
                    { axis: 'σ', value: (data.sovereignty_vector?.sigma || 0) / 100 },
                    { axis: 'λ', value: (data.sovereignty_vector?.lambda || 0) / 100 },
                  ]}
                >
                  <PolarGrid stroke="#475569" />
                  <PolarAngleAxis dataKey="axis" tick={{ fill: '#94a3b8', fontSize: 14 }} />
                  <Radar dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.5} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-[11px] text-slate-500">
              Échelle UI : valeurs /100 (0..100) — source fixed-point 0..10000
            </div>
          </div>

          {/* Meta */}
          <div className="space-y-4 text-sm">
            <div className="flex justify-between items-center p-3 bg-slate-800 rounded border border-slate-700">
              <span className="text-slate-400">Statut Réalité :</span>
              <span className="flex items-center gap-2 text-green-400 font-bold">
                <CheckCircle size={16} /> VALID (Integrity)
              </span>
            </div>

            <div className="p-3 bg-slate-800 rounded border border-slate-700">
              <span className="text-slate-400 block mb-1">Grade RGF (Gates) :</span>
              <span className="text-2xl font-black text-blue-400 tracking-tighter">
                {data.calculatedGrade}
              </span>
              <div className="text-[11px] text-slate-500 mt-1">Calculé localement (ne fait pas confiance à mesh).</div>
            </div>

            <div className="p-3 bg-slate-800 rounded border border-slate-700 font-mono text-[10px] break-all">
              <span className="text-slate-400 block mb-1 uppercase">Entropy Commitment :</span>
              {data.entropy?.commitment}
            </div>

            <div className="p-3 bg-slate-800 rounded border border-slate-700 font-mono text-[10px] break-all">
              <span className="text-slate-400 block mb-1 uppercase flex items-center gap-1">
                <Hash size={10} /> Receipt Hash (SHA3-256) :
              </span>
              <span className="text-green-400">{data.receipt_hash}</span>
              <div className="text-xs text-slate-500 mt-1">Comparé avec succès au hash canonique local (UTF-8).</div>
            </div>

            <div className="p-3 bg-slate-800 rounded border border-slate-700 text-[11px] text-slate-400">
              <div className="flex items-center gap-2">
                <KeyRound size={14} />
                Signature Ed25519 :
                <span className={`${sigColor} font-bold`}>{sigState.state}</span>
              </div>
              {sigState.detail && <div className="text-[10px] text-slate-500 mt-1">{sigState.detail}</div>}
            </div>
          </div>
        </div>
      )}
      {/* ✅ MODAL EDUCATIVE — Vérificateur d'Intégrité */}
      {isInfoOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(5px)', zIndex: 9999,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }} onClick={() => setIsInfoOpen(false)}>
          <div style={{
            background: 'rgba(20, 30, 50, 0.95)', border: '1px solid #0ff', padding: '30px', borderRadius: '16px',
            maxWidth: '600px', width: '90%', boxShadow: '0 0 50px rgba(0, 255, 255, 0.2)',
            color: '#fff', position: 'relative', maxHeight: '90vh', overflowY: 'auto'
          }} onClick={e => e.stopPropagation()}>

            <h2 style={{ borderBottom: '1px solid #333', paddingBottom: '10px', marginBottom: '20px', color: '#0ff' }}>
              Vérificateur d'Intégrité (Reality Verifier)
            </h2>

            <div style={{ textAlign: 'left', lineHeight: '1.6' }}>
              <h3 style={{ color: '#ffd369', marginTop: '15px' }}>1. C'est quoi ?</h3>
              <p style={{ color: '#ddd' }}>
                Ce portail public est l'outil d'audit ouvert de l'Oracle. Il permet à n'importe quel utilisateur ou
                tiers de confiance de vérifier <strong>mathématiquement</strong> l'authenticité d'une donnée certifiée
                par notre système, grâce à la cryptographie asymétrique (<strong>Ed25519</strong>).
              </p>

              <h3 style={{ color: '#ffd369', marginTop: '15px' }}>2. À quoi ça sert ?</h3>
              <ul style={{ color: '#ddd', paddingLeft: '20px' }}>
                <li><strong>Pour les Auditeurs :</strong> Prouver de manière irréfutable qu'une donnée ou un événement
                  d'entropie n'a pas été altéré depuis sa création.</li>
                <li><strong>Pour le Public :</strong> Garantir une transparence totale. "Don't trust, verify" : vous
                  n'avez pas besoin de faire confiance à notre infrastructure, vous pouvez vérifier la preuve
                  mathématique par vous-même.</li>
              </ul>

              <h3 style={{ color: '#ffd369', marginTop: '15px' }}>3. Comment l'utiliser ?</h3>
              <ul style={{ color: '#ddd', paddingLeft: '20px' }}>
                <li><strong>Le Reçu :</strong> Récupérez un "Reality Receipt" (un reçu cryptographique au format JWS)
                  généré lors d'un événement scellé par l'Oracle (ex: QUID ou Entropie).</li>
                <li><strong>Validation :</strong> Collez l'intégralité de ce reçu textuel dans le champ.</li>
                <li><strong>Action :</strong> Cliquez sur "Vérifier (Integrity + Ed25519)". Le système confrontera la
                  signature à la clé publique de l'Oracle et confirmera si le document est 100% authentique.</li>
              </ul>

              <div style={{ marginTop: '15px', background: '#111', padding: '10px', borderRadius: '8px', borderLeft: '3px solid #0ff' }}>
                <strong>Principe :</strong> Aucune donnée n'est envoyée à un serveur. La vérification est effectuée
                intégralement dans votre navigateur.
              </div>
            </div>

            <button onClick={() => setIsInfoOpen(false)} style={{
              marginTop: '25px', width: '100%', padding: '12px', background: '#333', color: '#fff',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
            }}>
              Fermer
            </button>

          </div>
        </div>
      )}
    </div>
  );
};

export default RealityVerifier;
